import { Injectable } from '@angular/core';

/**
 * Registra listeners de `touchstart` como PASSIVOS.
 *
 * Por quê: o `Renderer2.listen` do Angular registra tudo como não-passivo, e o
 * Chrome emite `[Violation] Added non-passive event listener to a
 * scroll-blocking 'touchstart' event` para cada um. Na Visão de Calendário isso
 * acontece uma vez por bloco de sessão, porque o `angular-draggable-droppable`
 * (usado pelo `mwlDraggable` do angular-calendar) registra `touchstart` em cada
 * elemento arrastável — e o número cresce a cada re-render da grade.
 *
 * Por que é seguro: o `onTouchStart` daquela lib NÃO chama `preventDefault()`;
 * o cancelamento do scroll durante o arraste é feito no `touchmove`, que ela
 * registra por conta própria com `passive: false` (fora do Renderer, portanto
 * fora do alcance deste plugin).
 *
 * Alcance: vale para qualquer listener de `touchstart` registrado VIA ANGULAR
 * (binding `(touchstart)` em template ou `Renderer2.listen`). Hoje o app não
 * tem nenhum — o único `touchstart` próprio é o do `app-popover`, registrado
 * direto com `addEventListener`, que este plugin não intercepta. Se algum dia
 * for preciso um `touchstart` que cancele o scroll, ele deve ser registrado
 * fora do Angular (como o popover faz).
 */
@Injectable()
export class PassiveTouchEventsPlugin {
  /** Preenchido pelo `EventManager` do Angular. */
  manager: any;

  supports(eventName: string): boolean {
    return eventName === 'touchstart';
  }

  addEventListener(element: HTMLElement, eventName: string, handler: (event: Event) => void): () => void {
    element.addEventListener(eventName, handler, { passive: true });
    return () => element.removeEventListener(eventName, handler);
  }

  addGlobalEventListener(target: string, eventName: string, handler: (event: Event) => void): () => void {
    const element: EventTarget = target === 'window' ? window : target === 'body' ? document.body : document;
    element.addEventListener(eventName, handler, { passive: true });
    return () => element.removeEventListener(eventName, handler);
  }
}
